/**
 * Coupon Modal Component
 *
 * 쿠폰 코드 등록 및 등록된 쿠폰 사용
 */

import { useEffect, useMemo, useState, useCallback } from 'react';
import { useBodyScrollLock } from '../hooks';
import { useLanguage } from '../i18n';
import { useAuth } from '../auth';
import { listRegisteredCoupons, redeemCoupon, registerCoupon, type RegisteredCouponItem } from '../api';
import { analytics } from '../analytics';

// 쿠폰 에러 코드 목록
const COUPON_ERROR_CODES = [
  'COUPON_CODE_REQUIRED',
  'COUPON_INVALID',
  'COUPON_NOT_REGISTERED',
  'COUPON_ALREADY_USED',
  'COUPON_INACTIVE',
  'COUPON_NOT_STARTED',
  'COUPON_EXPIRED',
  'COUPON_PLAN_TOO_HIGH',
  'COUPON_PLAN_MISMATCH',
  'COUPON_EXHAUSTED',
  'COUPON_BETA_PERIOD',
  'COUPON_PLAN_UNKNOWN',
  'USER_NOT_FOUND',
] as const;

type CouponErrorCode = typeof COUPON_ERROR_CODES[number];

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

export default function CouponModal({ isOpen, onClose }: Props) {
  const { t } = useLanguage();
  const { refreshQuota } = useAuth();

  const [code, setCode] = useState('');
  const [isRegistering, setIsRegistering] = useState(false);
  const [isUsing, setIsUsing] = useState<string | null>(null); // code
  const [items, setItems] = useState<RegisteredCouponItem[]>([]);
  const [isLoadingList, setIsLoadingList] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  useBodyScrollLock(isOpen);

  const normalizedCode = useMemo(() => code.trim().toUpperCase(), [code]);

  // 에러 코드를 번역된 메시지로 변환
  const translateErrorCode = useCallback((errorCode: string | null | undefined, fallbackMessage?: string): string => {
    if (errorCode && COUPON_ERROR_CODES.includes(errorCode as CouponErrorCode)) {
      const errors = t.coupon.errors as Record<string, string>;
      return errors[errorCode] || fallbackMessage || t.coupon.applyFailed;
    }
    return fallbackMessage || t.coupon.applyFailed;
  }, [t]);

  // API 에러에서 코드와 메시지 추출
  const extractApiError = useCallback((err: unknown): string => {
    if (err instanceof Error) {
      try {
        // JSON 형태의 에러 메시지 파싱 시도
        const parsed = JSON.parse(err.message);
        if (parsed.code) {
          return translateErrorCode(parsed.code, parsed.message);
        }
      } catch {
        // JSON이 아닌 경우 그대로 반환
        return err.message;
      }
    }
    return t.coupon.applyFailed;
  }, [t, translateErrorCode]);

  useEffect(() => {
    if (isOpen) {
      setCode('');
      setIsRegistering(false);
      setIsUsing(null);
      setError(null);
      setSuccessMsg(null);
    }
  }, [isOpen]);

  const loadList = useCallback(async () => {
    setIsLoadingList(true);
    try {
      const res = await listRegisteredCoupons();
      setItems(res.items || []);
    } catch (e) {
      const msg = e instanceof Error ? e.message : t.coupon.loadFailed;
      setError(msg);
    } finally {
      setIsLoadingList(false);
    }
  }, [t.coupon.loadFailed]);

  useEffect(() => {
    if (isOpen) {
      loadList();
    }
  }, [isOpen, loadList]);

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  };

  const handleRegister = async () => {
    if (!normalizedCode) return;
    setIsRegistering(true);
    setError(null);
    setSuccessMsg(null);
    analytics.couponRegisterAttempt();

    try {
      await registerCoupon(normalizedCode);
      setSuccessMsg(t.coupon.registered);
      analytics.couponRegisterSuccess();
      await loadList();
    } catch (e) {
      const msg = extractApiError(e);
      setError(msg);
      analytics.couponRegisterFail();
    } finally {
      setIsRegistering(false);
    }
  };

  const handleUse = async (couponCode: string) => {
    const c = couponCode.trim().toUpperCase();
    if (!c) return;
    setIsUsing(c);
    setError(null);
    setSuccessMsg(null);
    analytics.couponRedeemAttempt();

    try {
      const res = await redeemCoupon(c);
      await refreshQuota();
      const endsAt = res.subscription_ends_at ? ` (${res.subscription_ends_at})` : '';
      setSuccessMsg(`${t.coupon.applied}${endsAt}`);
      analytics.couponRedeemSuccess();
      await loadList();
    } catch (e) {
      const msg = extractApiError(e);
      setError(msg);
      analytics.couponRedeemFail();
    } finally {
      setIsUsing(null);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={handleBackdropClick}
    >
      <div className="bg-terminal-bg border border-terminal-border rounded-xl shadow-2xl w-full max-w-lg">
        {/* 헤더 */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-terminal-border">
          <h2 className="text-lg font-semibold text-terminal-text">{t.coupon.title}</h2>
          <button
            onClick={onClose}
            className="p-1 text-terminal-muted hover:text-terminal-text transition-colors"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* 내용 */}
        <div className="p-6 space-y-4">
          <p className="text-sm text-terminal-muted">{t.coupon.subtitle}</p>

          {error && (
            <div className="p-3 bg-status-nogo/10 border border-status-nogo/30 rounded-lg text-status-nogo text-sm">
              {error}
            </div>
          )}
          {successMsg && (
            <div className="p-3 bg-status-go/10 border border-status-go/30 rounded-lg text-status-go text-sm">
              {successMsg}
            </div>
          )}

          <div>
            <label className="block text-xs text-terminal-muted mb-1.5">
              {t.coupon.codeLabel}
            </label>
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder={t.coupon.codePlaceholder}
              className="w-full px-3 py-2.5 bg-terminal-dim/20 border border-terminal-border rounded-lg text-terminal-text text-sm placeholder:text-terminal-muted/50 focus:outline-none focus:border-status-go/50 transition-colors"
              autoComplete="off"
            />
          </div>

          <div className="flex items-center justify-end gap-2 pt-2">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-lg border border-terminal-border text-terminal-text text-sm hover:bg-terminal-dim/20 transition-colors"
              disabled={isRegistering || !!isUsing}
            >
              {t.coupon.close}
            </button>
            <button
              onClick={handleRegister}
              disabled={!normalizedCode || isRegistering}
              className="px-4 py-2 rounded-lg bg-status-go text-black text-sm font-semibold hover:bg-status-go/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isRegistering ? t.coupon.registering : t.coupon.register}
            </button>
          </div>

          {/* 등록된 쿠폰 목록 */}
          <div className="pt-2 border-t border-terminal-border/60">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-terminal-text">{t.coupon.registeredListTitle}</h3>
              <button
                onClick={loadList}
                className="text-xs text-terminal-muted hover:text-terminal-text transition-colors"
                disabled={isLoadingList}
              >
                {isLoadingList ? t.coupon.applying : 'Refresh'}
              </button>
            </div>

            {items.length === 0 ? (
              <div className="text-sm text-terminal-muted">
                {t.coupon.noRegistered}
              </div>
            ) : (
              <div className="space-y-2">
                {items.map((it) => {
                  const isUsed = !!it.used_at;
                  const canUse = it.usable && !isUsed;
                  const busy = isUsing === it.code;
                  return (
                    <div
                      key={it.code}
                      className="p-3 rounded-lg border border-terminal-border bg-terminal-dim/10 flex items-start justify-between gap-3"
                    >
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-terminal-text text-sm font-semibold truncate">{it.code}</span>
                          <span className="text-xs text-terminal-muted">
                            {it.plan.toUpperCase()} · +{it.days}d
                          </span>
                        </div>
                        {!canUse && (
                          <div
                            className={`text-xs mt-1 ${
                              isUsed ? 'text-terminal-muted' : 'text-status-nogo'
                            }`}
                          >
                            {isUsed ? t.coupon.used : translateErrorCode(it.unusable_code, it.unusable_reason || '')}
                          </div>
                        )}
                      </div>
                      <button
                        onClick={() => handleUse(it.code)}
                        disabled={!canUse || busy || isRegistering}
                        className="px-3 py-1.5 rounded-md bg-terminal-dim/30 text-terminal-text text-xs font-semibold hover:bg-terminal-dim/40 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-shrink-0"
                      >
                        {busy ? t.coupon.using : t.coupon.use}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}


